import { Drawer, Stack, Group, TextInput, ActionIcon } from "@mantine/core"
import { useForm } from "@mantine/form"
import { IconUser, IconAt, IconX, IconPlus } from "@tabler/icons-react"
import { Button } from "@tfg/ui"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { standardEmail } from "@tfg/types/schemas/utils"

import { queryClient } from "~/backend/queryClient"
import { useInviteUserByOrganizationn } from "~/gen/api/hooks/useInviteUserByOrganizationn"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"
import { FindOrganizationByIdResponseDto } from "~/gen/api"

const inviteUsersSchema = z.object({
  users: z.array(
    z.object({
      name: z.string().min(1),
      email: standardEmail(),
    })
  ),
})

type InviteUsersForm = z.infer<typeof inviteUsersSchema>

interface Props {
  opened: boolean
  organization: FindOrganizationByIdResponseDto
  onClose(): void
}

export const InviteUsersDrawer = ({ opened, organization, onClose }: Props) => {
  const { t } = useTranslation("organizationSettings")
  const { mutate: inviteUsers, isPending: isInviting } =
    useInviteUserByOrganizationn({
      mutation: {
        onSuccess: () => {
          showSuccessNotification({
            title: t("inviteUsers.success"),
            message: t("inviteUsers.success"),
          })
          queryClient.invalidateQueries({ queryKey: ["organizations"] })
          onClose()
        },
        onError: (error) => {
          showAxiosError({
            error: error as Error,
            notificationTitle: t("inviteUsers.error"),
            notificationMessage: t("inviteUsers.error"),
            showNotification: true,
          })
        },
      },
    })

  const form = useForm<InviteUsersForm>({
    validate: zod4Resolver(inviteUsersSchema),
    initialValues: {
      users: [
        {
          name: "",
          email: "",
        },
      ],
    },
    validateInputOnChange: true,
    validateInputOnBlur: true,
  })

  const handleInviteUsers = (values: InviteUsersForm) => {
    inviteUsers({
      organizationId: organization.id,
      data: {
        users: values.users,
      },
    })
  }

  return (
    <Drawer
      position="right"
      size="lg"
      opened={opened}
      onClose={onClose}
      title="Create Users"
    >
      <form onSubmit={form.onSubmit(handleInviteUsers)}>
        <Stack gap="sm" align="start" h="100%">
          {form.values.users.map((_, index) => (
            <Stack key={index}>
              <Group w="100%" align="start">
                <TextInput
                  placeholder="User name"
                  label="Name"
                  leftSection={<IconUser size={16} />}
                  {...(() => {
                    const inputProps = form.getInputProps(`users.${index}.name`)
                    return {
                      ...inputProps,
                      onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
                        inputProps.onBlur?.(event)
                        form.validateField(`users.${index}.name`)
                      },
                      onChange: (
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        inputProps.onChange(event)
                        form.validateField(`users.${index}.name`)
                      },
                    }
                  })()}
                />
                <TextInput
                  label="Email"
                  placeholder="Email address"
                  leftSection={<IconAt size={16} />}
                  {...(() => {
                    const inputProps = form.getInputProps(
                      `users.${index}.email`
                    )
                    return {
                      ...inputProps,
                      onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
                        inputProps.onBlur?.(event)
                        // form.setTouched(`users.${index}.email`, true)
                        form.validateField(`users.${index}.email`)
                      },
                      onChange: (
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        inputProps.onChange(event)
                        // form.setTouched(`users.${index}.email`, true)
                        form.validateField(`users.${index}.email`)
                      },
                    }
                  })()}
                />
                <ActionIcon
                  mt={26}
                  onClick={() => form.removeListItem("users", index)}
                >
                  <IconX />
                </ActionIcon>
              </Group>
            </Stack>
          ))}
          <Group justify="apart">
            <Button
              onClick={() =>
                form.insertListItem("users", { name: "", email: "" })
              }
              icon={<IconPlus size={16} />}
              variant="tertiary"
            >
              Add one
            </Button>
            <Button
              loading={isInviting}
              type="submit"
              disabled={!form.isValid()}
            >
              Create users
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  )
}
